import React, { Suspense } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { usePrivacyPreferences } from '@contexts/PrivacyPreferencesContext'
import { trackPageView } from '@services/analyticsClient'
import { isSensitiveTelemetryRoute } from '@/privacy/telemetrySanitizer'
import { useWebVitals } from './hooks/useWebVitals'

// Components
import LoadingSpinner from '@components/ui/LoadingSpinner'
import ErrorBoundary from '@components/ui/ErrorBoundary'
import BlogSkeleton from '@components/ui/BlogSkeleton'
import PostSkeleton from '@components/ui/PostSkeleton'
import OfflineIndicator from '@components/ui/OfflineIndicator'
import ProtectedRoute from '@components/auth/ProtectedRoute'
import AdminContentMigrationBoundary from '@components/blog/admin/AdminContentMigrationBoundary'
import PublicRouteLayout from '@components/layout/PublicRouteLayout'
import CookieConsent from '@components/privacy/CookieConsent'
import RouteIndexingMeta from '@components/seo/RouteIndexingMeta'

// Lazy load pages for code splitting
const HomePage = React.lazy(() => import('@pages/HomePage'))
const LoginPage = React.lazy(() => import('@pages/LoginPage'))
const SignupPage = React.lazy(() => import('@pages/SignupPage'))
const NotFoundPage = React.lazy(() => import('@pages/NotFoundPage'))
const ForgotPasswordPage = React.lazy(() => import('@pages/ForgotPasswordPage'))
const ResetPasswordPage = React.lazy(() => import('@pages/ResetPasswordPage'))
const VerifyEmailPage = React.lazy(() => import('@pages/VerifyEmailPage'))

// Lazy load landing page routes
const FeaturesPage = React.lazy(() => import('@pages/FeaturesPage'))
const PricingPage = React.lazy(() => import('@pages/PricingPage'))
const ApiDocsPage = React.lazy(() => import('@pages/ApiDocsPage'))
const AboutPage = React.lazy(() => import('@pages/AboutPage'))
const CareersPage = React.lazy(() => import('@pages/CareersPage'))
const ContactPage = React.lazy(() => import('@pages/ContactPage'))
const PrivacyPage = React.lazy(() => import('@pages/PrivacyPage'))
const CookiePolicyPage = React.lazy(() => import('@pages/CookiePolicyPage'))
const TermsPage = React.lazy(() => import('@pages/TermsPage'))
const SecurityPage = React.lazy(() => import('@pages/SecurityPage'))
const CompliancePage = React.lazy(() => import('@pages/CompliancePage'))

// Lazy load blog pages
const BlogHome = React.lazy(() => import('@pages/blog/BlogHome'))
const BlogPost = React.lazy(() => import('@pages/blog/BlogPost'))
const BlogCategory = React.lazy(() => import('@pages/blog/BlogCategory'))
const BlogTag = React.lazy(() => import('@pages/blog/BlogTag'))
const BlogSearch = React.lazy(() => import('@pages/blog/BlogSearch'))
const BlogAuthor = React.lazy(() => import('@pages/blog/BlogAuthor'))
const ReadingList = React.lazy(() => import('@pages/blog/ReadingList'))
const VerifySubscription = React.lazy(() => import('@pages/blog/VerifySubscription'))
const Unsubscribe = React.lazy(() => import('@pages/blog/Unsubscribe'))
const SubscriptionPreferences = React.lazy(() => import('@pages/blog/SubscriptionPreferences'))

function RouteFallback() {
  return (
    <div
      className="flex min-h-screen items-center justify-center bg-[var(--x3-page)] text-[var(--x3-text)]"
      aria-busy="true"
    >
      <LoadingSpinner size="lg" />
    </div>
  )
}

/**
 * Main App Component
 * Handles routing and authentication state
 */
function App() {
  const location = useLocation()
  const { categories } = usePrivacyPreferences()
  const analyticsAllowed =
    categories.analytics &&
    !isSensitiveTelemetryRoute(`${location.pathname}${location.search}`)
  
  // Track Core Web Vitals
  useWebVitals(analyticsAllowed)
  
  // Track page views on route changes (only for authenticated users)
  React.useEffect(() => {
    // Track page view when location changes
    // This runs once per route change, not on every render
    if (analyticsAllowed) trackPageView(location.pathname)
  }, [analyticsAllowed, location.pathname])

  return (
    <ErrorBoundary>
      <RouteIndexingMeta />
      <OfflineIndicator />
      <div className="min-h-screen bg-[var(--x3-page)] text-[var(--x3-text)]">
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Authentication Routes */}
            <Route 
              path="/login" 
              element={<LoginPage />} 
            />
            <Route 
              path="/signup" 
              element={<SignupPage />} 
            />
            <Route 
              path="/forgot-password" 
              element={<ForgotPasswordPage />} 
            />
            <Route 
              path="/reset-password" 
              element={<ResetPasswordPage />} 
            />
            <Route path="/verify-email" element={<VerifyEmailPage />} />

            {/* Persistent public marketing shell */}
            <Route element={<PublicRouteLayout />}>
              <Route
                path="/"
                element={<HomePage />}
              />
              <Route
                path="/features"
                element={<FeaturesPage />}
              />
              <Route
                path="/pricing"
                element={<PricingPage />}
              />
              <Route path="/developers" element={<ApiDocsPage />} />
              <Route path="/api-docs" element={<Navigate to="/developers" replace />} />
              <Route
                path="/about"
                element={<AboutPage />}
              />
              <Route
                path="/careers"
                element={<CareersPage />}
              />
              <Route
                path="/contact"
                element={<ContactPage />}
              />
              <Route
                path="/privacy"
                element={<PrivacyPage />}
              />
              <Route
                path="/cookies"
                element={<CookiePolicyPage />}
              />
              <Route path="/cookie-policy" element={<Navigate to="/cookies" replace />} />
              <Route
                path="/terms"
                element={<TermsPage />}
              />
              <Route
                path="/security"
                element={<SecurityPage />}
              />
              <Route
                path="/compliance"
                element={<CompliancePage />}
              />

              {/* Editorial blog routes share the persistent public shell. */}
              <Route
                path="/blog"
                element={
                  <Suspense fallback={<BlogSkeleton />}>
                    <BlogHome />
                  </Suspense>
                }
              />
              <Route
                path="/blog/search"
                element={
                  <Suspense fallback={<BlogSkeleton />}>
                    <BlogSearch />
                  </Suspense>
                }
              />
              <Route
                path="/blog/category/:slug"
                element={
                  <Suspense fallback={<BlogSkeleton />}>
                    <BlogCategory />
                  </Suspense>
                }
              />
              <Route
                path="/blog/tag/:slug"
                element={
                  <Suspense fallback={<BlogSkeleton />}>
                    <BlogTag />
                  </Suspense>
                }
              />
              <Route
                path="/blog/author/:authorId"
                element={
                  <Suspense fallback={<BlogSkeleton />}>
                    <BlogAuthor />
                  </Suspense>
                }
              />
              <Route
                path="/blog/reading-list"
                element={
                  <Suspense fallback={<BlogSkeleton />}>
                    <ReadingList />
                  </Suspense>
                }
              />
              <Route
                path="/blog/:slug"
                element={
                  <Suspense fallback={<PostSkeleton />}>
                    <BlogPost />
                  </Suspense>
                }
              />
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            {/* Blog subscription routes retain their focused transaction shell. */}
            <Route 
              path="/blog/subscribe/verify/:token" 
              element={
                <Suspense fallback={<RouteFallback />}>
                  <VerifySubscription />
                </Suspense>
              } 
            />
            <Route 
              path="/blog/unsubscribe/:token" 
              element={
                <Suspense fallback={<RouteFallback />}>
                  <Unsubscribe />
                </Suspense>
              } 
            />
            <Route 
              path="/blog/preferences" 
              element={
                <Suspense fallback={<RouteFallback />}>
                  <SubscriptionPreferences />
                </Suspense>
              } 
            />

            {/* Retired blog-admin URLs redirect authorized administrators. */}
            <Route
              path="/blog/admin/*"
              element={
                <ProtectedRoute allowedRoles={['admin', 'superAdmin']} redirectTo="/blog">
                  <AdminContentMigrationBoundary />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Suspense>
      </div>
      <CookieConsent />
    </ErrorBoundary>
  )
}

export default App
